import './StackedAreaChart.scss';
import {
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
  useMemo,
  ReactElement,
  ReactNode,
  RefObject,
  FC
} from 'react';
import { AutoSizer } from 'react-virtualized-auto-sizer';
import { Area, AreaChart as AC, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts';
import {
  CARTESIAN_GRID_STROKE,
  CARTESIAN_GRID_STROKE_WIDTH,
  TICK_STYLE
} from '@Components/Charts/ChartConstants';
import * as R from 'ramda';
import { ChartTooltip } from '@Components/Charts/ChartTooltip/ChartTooltip';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { headerFormatter, useDynamicYAxisWidth } from '@Components/Charts/ChartUtils';
import { getColorMap as colors, Neutral200 } from '../../../utils/colors';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import IncrementalityIcon from '../../../assets/IncrementalityIcon.svg?react';

const INACTIVE_COLOR = Neutral200;

interface AreaChartTooltipContentProps {
  formatHeaderLabel: (label: string) => string;
  valueFormatter?: (value: any) => string;
  // recharts injects these when it clones the content element.
  label?: string | number;
  payload?: any[];
}

// Hoisted out of StackedAreaChart so recharts doesn't see a new component type
// on every render. recharts clones this element, injecting label/payload.
const AreaChartTooltipContent = ({
  formatHeaderLabel,
  valueFormatter,
  label,
  payload
}: AreaChartTooltipContentProps): ReactElement | null => {
  if (!payload || !label) {
    return null;
  }
  const formattedHeaderLabel = formatHeaderLabel(String(label));
  const items = R.sortBy(
    (item: any) => -Math.abs(item.value),
    payload.map(item => ({
      label: item.name as string,
      value: valueFormatter ? valueFormatter(item.value) : (item.value as number),
      color: item.stroke
    }))
  );
  return <ChartTooltip headerLabel={formattedHeaderLabel} items={items} />;
};

interface PortaledAreaChartTooltipProps {
  anchorRef: RefObject<HTMLDivElement | null>;
  formatHeaderLabel: (label: string) => string;
  valueFormatter?: (value: any) => string;
  // recharts injects these when it clones the content element.
  active?: boolean;
  label?: string | number;
  payload?: any[];
  coordinate?: { x?: number; y?: number };
}

// Renders the tooltip into a viewport-positioned portal (via `<Tooltip portal={document.body}>`) so
// it escapes the card's `overflow: hidden` clip and floats above everything. Hoisted to module scope
// (not defined during render) so recharts sees a stable component type when it clones the content.
const PortaledAreaChartTooltip = ({
  anchorRef,
  formatHeaderLabel,
  valueFormatter,
  active,
  label,
  payload,
  coordinate
}: PortaledAreaChartTooltipProps): ReactElement | null => {
  if (!active || !payload || !label) {
    return null;
  }
  return (
    <StackedAreaTooltipPortal anchorRef={anchorRef} coordinate={coordinate}>
      <AreaChartTooltipContent
        formatHeaderLabel={formatHeaderLabel}
        valueFormatter={valueFormatter}
        label={label}
        payload={payload}
      />
    </StackedAreaTooltipPortal>
  );
};

interface AreaChartProps {
  areas: {
    dataKey: string;
    incrementality: boolean;

    name: string;
  }[];
  chart1Title?: string;
  chart2Title?: string;
  data1: any[];
  data2: any[];
  dateGrouping?: string;
  groupedAreas?: any[];
  hardCodedColorName?: string;
  hardCodedColor?: string;
  hideLegend?: boolean;
  legendTitle?: string | null;
  tooltipFormatterTop?: (value: any) => string;
  tooltipFormatterBottom?: (value: any) => string;
  usePercentageYAxis?: boolean;
  xAxisDataKey: string;
  xAxisTickFormatter?: (value: any) => string;
  yAxisDataKey?: string;
  yAxisTickFormatter1?: (value: any) => string;
  yAxisTickFormatter2?: (value: any) => string;
  // Clamp the bottom (chart2 / "Effect") y-axis to a non-negative range so it never renders values
  // below 0. Off by default (existing callers keep the auto domain that can dip negative).
  chart2NonNegativeYAxis?: boolean;
}

export const StackedAreaChart: FC<AreaChartProps> = ({
  areas,
  chart1Title,
  chart2Title,
  data1,
  data2,
  dateGrouping,
  groupedAreas,
  hardCodedColorName,
  hardCodedColor,
  hideLegend,
  legendTitle,
  tooltipFormatterTop,
  tooltipFormatterBottom,
  usePercentageYAxis = false,
  xAxisDataKey,
  xAxisTickFormatter,
  yAxisDataKey,
  yAxisTickFormatter1,
  yAxisTickFormatter2,
  chart2NonNegativeYAxis = false
}) => {
  const { fiscal_cal: fiscalCal } = useCompanyInfo();
  // The tooltips are portaled to document.body (below) so they escape the card's `overflow: hidden`
  // clip and float over everything. We measure each chart's on-screen box to translate recharts'
  // chart-relative `coordinate` into viewport coordinates for the portaled tooltip.
  const chart1ContainerRef = useRef<HTMLDivElement>(null);
  const chart2ContainerRef = useRef<HTMLDivElement>(null);
  const [activeArea, setActiveArea] = useState(null);
  const [activeGroup, setActiveGroup] = useState(null);
  const [hoveredLegend, setHoveredLegend] = useState(null);
  const [covariateSelected, setCovariateSelected] = useState(false);

  const colorMap = useMemo(() => {
    if (!R.isEmpty(areas)) {
      return colors(areas, hardCodedColorName, hardCodedColor, undefined);
    }
    return undefined;
  }, [areas, hardCodedColor, hardCodedColorName]);

  const { xAxisHeight, yAxisWidth, setChartRef } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    reCalcDependency: data1
  });
  const {
    xAxisHeight: xAxisHeight2,
    yAxisWidth: yAxisWidth2,
    setChartRef: setChartRef2
  } = useDynamicYAxisWidth({
    yAxisWidthModifier: x => x + 7,
    reCalcDependency: data2
  });

  const handleLegendClick = useCallback(
    (area, group?) => {
      if (activeArea === area.name) {
        setActiveArea(null);
        setActiveGroup(null);
      } else {
        setActiveArea(area.name);
        if (group) {
          setActiveGroup(group.channel);
        }
      }
    },
    [activeArea]
  );

  const handleLegendEnter = useCallback(
    line => {
      setHoveredLegend(hoveredLegend === line.name ? null : line.name);
    },
    [hoveredLegend]
  );

  const percentageYAxis = (
    <YAxis
      dataKey={yAxisDataKey}
      axisLine={false}
      domain={[0, 100]}
      orientation='left'
      tick={TICK_STYLE}
      ticks={[0, 25, 50, 75, 100]}
      type='number'
      yAxisId='0'
      unit='%'
      width={Math.max(yAxisWidth, yAxisWidth2)}
    />
  );

  const formatHeaderLabel = useCallback(
    (label: string) => headerFormatter(dateGrouping, label, fiscalCal),
    [dateGrouping, fiscalCal]
  );

  const buildLegend = useCallback(
    legendAreas =>
      legendAreas.map(area => {
        const isActiveArea = activeArea === null ? true : activeArea === area.name;
        const isHoveredLegend = hoveredLegend === null ? true : hoveredLegend === area.name;
        const activeColor = isActiveArea ? colorMap[area.name] : INACTIVE_COLOR;
        const hoverColor = isHoveredLegend ? colorMap[area.name] : INACTIVE_COLOR;
        return (
          <InteractiveDiv
            className='legend-item'
            key={area.name}
            onClick={() => {
              setCovariateSelected(area.name === 'Covariates' && isActiveArea);
              handleLegendClick(area);
            }}
            onMouseEnter={() => handleLegendEnter(area)}
            onMouseLeave={() => setHoveredLegend(null)}
          >
            <div className='legend-value'>
              <div className={isActiveArea ? '' : 'inactive-legend'}>
                {(!area.grouping ||
                  !(
                    area.grouping.platform ||
                    area.grouping.tactic ||
                    area.grouping.brand_nonbrand
                  )) && (
                  <div className='legend-row'>
                    <div
                      className='square'
                      style={{
                        backgroundColor: `${activeArea ? activeColor : hoverColor}`
                      }}
                    />
                    <div className='legend-header'>
                      <span>
                        {area.grouping ? area.grouping.channel : area.name}
                        {area.incrementality && (
                          <>
                            {'\u00A0'}
                            <IncrementalityIcon
                              className={`incrementality-icon ${
                                isActiveArea || activeArea === null ? 'active' : 'inactive'
                              }`}
                            />
                          </>
                        )}
                      </span>
                    </div>
                  </div>
                )}
                {area.grouping &&
                  (area.grouping.platform ||
                    area.grouping.tactic ||
                    area.grouping.brand_nonbrand) && (
                    <>
                      <div className='legend-header-wrapper'>
                        <div className='legend-header'>
                          <span>
                            {area.grouping.channel}
                            {area.incrementality && (
                              <>
                                {'\u00A0'}
                                <IncrementalityIcon
                                  className={`incrementality-icon ${
                                    isActiveArea || activeArea === null ? 'active' : 'inactive'
                                  }`}
                                />
                              </>
                            )}
                          </span>
                        </div>
                      </div>
                      <div className='sub-header-wrapper'>
                        <div
                          className='square'
                          style={{
                            backgroundColor: `${activeArea ? activeColor : hoverColor}`
                          }}
                        />
                        <div className='sub-header1'>{area.grouping.platform}</div>
                        {area.grouping.platform &&
                          (area.grouping.tactic || area.grouping.brand_nonbrand) && (
                            <div style={{ whiteSpace: 'pre-wrap' }}> – </div>
                          )}
                        <div className='sub-header2'>{`${
                          area.grouping.tactic ? area.grouping.tactic : ''
                        }${area.grouping.tactic && area.grouping.brand_nonbrand ? ', ' : ''}${
                          area.grouping.brand_nonbrand ? area.grouping.brand_nonbrand : ''
                        }`}</div>
                      </div>
                    </>
                  )}
              </div>
            </div>
          </InteractiveDiv>
        );
      }),
    [activeArea, colorMap, handleLegendClick, handleLegendEnter, hoveredLegend]
  );

  const buildLegendByGroupedArea = useCallback(
    legendGroups =>
      legendGroups.map(group => (
        <div key={group.channel} className='legend-item'>
          <div className='legend-value'>
            <div>
              <div className='legend-header-wrapper' style={{ cursor: 'default' }}>
                <div
                  className={`legend-header ${
                    activeGroup === group.channel || activeGroup === null ? '' : 'inactive-legend'
                  }`}
                >
                  {group.channel}
                </div>
              </div>
              {group.areas.map((area, areaIndex) => {
                const isActiveArea = activeArea === null ? true : activeArea === area.name;
                const isHoveredLegend = hoveredLegend === null ? true : hoveredLegend === area.name;
                const activeColor = isActiveArea ? colorMap[area.name] : INACTIVE_COLOR;
                const hoverColor = isHoveredLegend ? colorMap[area.name] : INACTIVE_COLOR;
                return (
                  <InteractiveDiv
                    key={`${group.channel}-${area.name}-${areaIndex}`}
                    className='sub-header-wrapper'
                    onClick={() => {
                      setCovariateSelected(area.name === 'Covariates' && isActiveArea);
                      handleLegendClick(area, group);
                    }}
                    onMouseEnter={() => handleLegendEnter(area)}
                    onMouseLeave={() => setHoveredLegend(null)}
                  >
                    <div
                      className='square'
                      style={{
                        backgroundColor: `${activeArea ? activeColor : hoverColor}`
                      }}
                    />
                    <div className='sub-header-text-wrap'>
                      <div className={`sub-header1 ${isActiveArea ? '' : 'inactive-legend'}`}>
                        <span>
                          {area.grouping.platform}
                          {area.incrementality &&
                            !(area.grouping.tactic || area.grouping.brand_nonbrand) && (
                              <>
                                {' '}
                                <IncrementalityIcon
                                  className={`incrementality-icon ${
                                    isActiveArea || activeArea === null ? 'active' : 'inactive'
                                  }`}
                                />
                              </>
                            )}
                        </span>
                      </div>
                      {area.grouping.platform &&
                        (area.grouping.tactic || area.grouping.brand_nonbrand) && (
                          <div style={{ whiteSpace: 'pre-wrap' }}> – </div>
                        )}
                      <div className={`sub-header2 ${isActiveArea ? '' : 'inactive-legend'}`}>
                        <span>
                          {`${area.grouping.tactic ? area.grouping.tactic : ''}${
                            area.grouping.tactic && area.grouping.brand_nonbrand ? ', ' : ''
                          }${area.grouping.brand_nonbrand ? area.grouping.brand_nonbrand : ''}`}
                          {area.incrementality &&
                            (area.grouping.tactic || area.grouping.brand_nonbrand) && (
                              <>
                                {' '}
                                <IncrementalityIcon
                                  className={`incrementality-icon ${
                                    isActiveArea || activeArea === null ? 'active' : 'inactive'
                                  }`}
                                />
                              </>
                            )}
                        </span>
                      </div>
                    </div>
                  </InteractiveDiv>
                );
              })}
            </div>
          </div>
        </div>
      )),
    [activeArea, activeGroup, colorMap, handleLegendClick, handleLegendEnter, hoveredLegend]
  );

  return (
    <div className='wrapper'>
      <div className='area-charts-wrapper'>
        <div className='area-chart stacked-area-chart1'>
          <div className='chart1-title'>{chart1Title}</div>
          <div style={{ display: 'flex', flex: 1 }} ref={chart1ContainerRef}>
            <AutoSizer
              renderProp={({ width, height }) => (
                <AC data={data1} height={height} ref={setChartRef} width={width}>
                  <CartesianGrid
                    stroke={CARTESIAN_GRID_STROKE}
                    strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                    vertical={false}
                  />
                  <XAxis
                    axisLine={false}
                    dataKey={xAxisDataKey}
                    height={Math.max(xAxisHeight, 30)}
                    tick={TICK_STYLE}
                    tickFormatter={xAxisTickFormatter}
                  />
                  {usePercentageYAxis && percentageYAxis}
                  {!usePercentageYAxis && (
                    <YAxis
                      axisLine={false}
                      dataKey={yAxisDataKey}
                      tick={TICK_STYLE}
                      tickFormatter={yAxisTickFormatter1}
                      tickLine={false}
                      width={Math.max(yAxisWidth, yAxisWidth2)}
                    />
                  )}
                  {areas.map(area => {
                    const isActiveArea = activeArea === null ? true : activeArea === area.name;
                    const isHoveredLegend =
                      hoveredLegend === null ? true : hoveredLegend === area.name;
                    const activeColor = isActiveArea ? colorMap[area.name] : INACTIVE_COLOR;
                    const hoverColor = isHoveredLegend ? colorMap[area.name] : INACTIVE_COLOR;
                    if (!isActiveArea && activeArea !== null && !covariateSelected) {
                      return null;
                    }
                    return (
                      <Area
                        activeDot={false}
                        dataKey={area.dataKey}
                        fill={activeArea ? activeColor : hoverColor}
                        fillOpacity={1}
                        isAnimationActive={false}
                        key={area.name}
                        name={area.name}
                        stackId='1'
                        stroke={activeArea ? activeColor : hoverColor}
                        strokeWidth={0}
                        type='monotone'
                      />
                    );
                  })}
                  <Tooltip
                    portal={document.body}
                    content={
                      <PortaledAreaChartTooltip
                        anchorRef={chart1ContainerRef}
                        formatHeaderLabel={formatHeaderLabel}
                        valueFormatter={tooltipFormatterTop}
                      />
                    }
                    isAnimationActive={false}
                  />
                </AC>
              )}
            />
          </div>
        </div>
        <div className='area-chart stacked-area-chart2'>
          <div className='chart2-title'>{chart2Title}</div>
          <div style={{ display: 'flex', flex: 1 }} ref={chart2ContainerRef}>
            <AutoSizer
              renderProp={({ width, height }) => (
                <AC data={data2} height={height} ref={setChartRef2} width={width}>
                  <CartesianGrid
                    stroke={CARTESIAN_GRID_STROKE}
                    strokeWidth={CARTESIAN_GRID_STROKE_WIDTH}
                    vertical={false}
                  />
                  <XAxis
                    axisLine={false}
                    dataKey={xAxisDataKey}
                    height={Math.max(xAxisHeight2, 30)}
                    tick={TICK_STYLE}
                    tickFormatter={xAxisTickFormatter}
                  />
                  {usePercentageYAxis && percentageYAxis}
                  {!usePercentageYAxis && (
                    <YAxis
                      axisLine={false}
                      dataKey={yAxisDataKey}
                      domain={chart2NonNegativeYAxis ? [0, 'auto'] : undefined}
                      allowDataOverflow={chart2NonNegativeYAxis}
                      tick={TICK_STYLE}
                      tickFormatter={yAxisTickFormatter2}
                      tickLine={false}
                      width={Math.max(yAxisWidth, yAxisWidth2)}
                    />
                  )}
                  {areas.map(area => {
                    const isActiveArea = activeArea === null ? true : activeArea === area.name;
                    const isHoveredLegend =
                      hoveredLegend === null ? true : hoveredLegend === area.name;
                    const activeColor = isActiveArea ? colorMap[area.name] : INACTIVE_COLOR;
                    const hoverColor = isHoveredLegend ? colorMap[area.name] : INACTIVE_COLOR;
                    if (!isActiveArea && activeArea !== null) {
                      return null;
                    }
                    return (
                      <Area
                        activeDot={false}
                        dataKey={area.dataKey}
                        fill={activeArea ? activeColor : hoverColor}
                        fillOpacity={1}
                        isAnimationActive={false}
                        key={area.name}
                        name={area.name}
                        stackId='1'
                        stroke={activeArea ? activeColor : hoverColor}
                        strokeWidth={0}
                        type='monotone'
                      />
                    );
                  })}
                  <Tooltip
                    portal={document.body}
                    content={
                      <PortaledAreaChartTooltip
                        anchorRef={chart2ContainerRef}
                        formatHeaderLabel={formatHeaderLabel}
                        valueFormatter={tooltipFormatterBottom}
                      />
                    }
                    isAnimationActive={false}
                  />
                </AC>
              )}
            />
          </div>
        </div>
      </div>
      <div className={`right-of-chart ${hideLegend ? 'hide' : ''}`}>
        <div className='legend-title'>{legendTitle}</div>
        <div className='legend'>
          {(!groupedAreas || R.isEmpty(groupedAreas)) && buildLegend(areas)}
          {groupedAreas && buildLegendByGroupedArea(groupedAreas)}
        </div>
      </div>
    </div>
  );
};

interface StackedAreaTooltipPortalProps {
  // Ref to the chart's on-screen box; recharts' `coordinate` is relative to that box's top-left. Read
  // inside the effect (never during render) to translate `coordinate` into viewport coordinates.
  anchorRef: RefObject<HTMLDivElement | null>;
  coordinate: { x?: number; y?: number } | undefined;
  children: ReactNode;
}

// Positions the portaled tooltip at the cursor in viewport coordinates, clamped to stay fully on
// screen. Rendered into document.body (via `<Tooltip portal>`), so it escapes the card's
// `overflow: hidden` clip and floats above everything regardless of its height. recharts 3.10
// disables its own auto-positioning once `portal` is passed, so we position manually here.
const StackedAreaTooltipPortal = ({
  anchorRef,
  coordinate,
  children
}: StackedAreaTooltipPortalProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);

  useLayoutEffect(() => {
    const anchorRect = anchorRef.current?.getBoundingClientRect();
    if (!ref.current || !anchorRect || !coordinate) {
      return;
    }
    const { width, height } = ref.current.getBoundingClientRect();
    const margin = 12;
    const anchorX = anchorRect.left + (coordinate.x ?? 0);
    const anchorY = anchorRect.top + (coordinate.y ?? 0);

    // Prefer opening to the right of the cursor; flip left if it would overflow the viewport.
    let left = anchorX + margin;
    if (left + width > window.innerWidth - margin) {
      left = anchorX - width - margin;
    }
    left = Math.max(margin, left);

    // Anchor near the cursor vertically, then clamp so the whole tooltip stays on screen.
    let top = anchorY + margin;
    top = Math.min(top, window.innerHeight - height - margin);
    top = Math.max(margin, top);

    setPosition({ left, top });
  }, [anchorRef, coordinate]);

  return (
    <div
      ref={ref}
      className='stacked-area-tooltip-portal'
      style={{
        left: position?.left ?? 0,
        top: position?.top ?? 0,
        visibility: position ? 'visible' : 'hidden'
      }}
    >
      {children}
    </div>
  );
};
